// Puts the scene's sims on screen. When the scene or a choice changes, it resolves each slot's
// candidates (the first run that exists wins), opens the readers, builds the per-structure
// overlays and writes the result to the store; the bridge sends the viewport commands from there.

import { useAtlas, type DrugState, type SimSlot, type SimsState, type StrokeState } from './store.ts';
import { scenarioById, type ChoiceOption, type Scenario, type Timeline } from '../scenarios/index.ts';
import { defaultSource, type SimSource } from '../data/sims/files.ts';
import { annotationOf, anyExists, isStandIn, readIndex, resolveFirst, type OpenSim } from '../data/sims/discover.ts';
import { dynamicsOverlay, hemoOverlay, pharmaOverlay, regionOverlay, schaeferColumn, type Overlay } from '../data/sims/overlay.ts';
import { seizureOnset } from '../data/sims/inspect.ts';
import { crosswalk, HD_CORTEX, packSource } from '../data/sims/crosswalk.ts';
import { pkCourse, type BindingCurve, type PharmaSim, type PkCourse } from '../data/sims/pharma.ts';
import { courseDose, courseFits, scaledCourse, scaledEngagement, type CourseDose } from '../data/sims/dose.ts';
import { defaultSpeed, sceneTimelineOf } from './scene.ts';
import { choiceKey, strokeChoices } from './stroke.ts';

let source: SimSource = defaultSource();
/** Where packs/ comes from: the HD cortex's label layers make pharma's DKT crosswalk. */
let packs: SimSource = packSource();
let generation = 0;
/** The opened readers for the current scene, by slot. */
const opened = new Map<string, OpenSim>();

export function openedSlot(slot: string): OpenSim | undefined {
  return opened.get(slot);
}

/** Fill `{id}` placeholders with choice values; null when one has no value. */
export function expand(candidate: string, values: Record<string, string>): string | null {
  let missing = false;
  const out = candidate.replace(/\{([a-z0-9_-]+)\}/gi, (_, id: string) => {
    const v = values[id];
    if (v === undefined) missing = true;
    return v ?? '';
  });
  return missing ? null : out;
}

type Option = ChoiceOption & { available: boolean; id?: string };
type Options = Record<string, Option[]>;

/**
 * Index options with `supersede` applied: a listed run that stands in for older ones takes the place
 * and name of the first of them that's listed (keeping its own label as the hint), and the others
 * leave the list. Also returns which run each removed id now resolves to.
 */
export function supersede(opts: Option[], rules: Record<string, string[]> = {}): { options: Option[]; replaced: Map<string, string> } {
  let out = opts;
  const replaced = new Map<string, string>();
  for (const [by, olds] of Object.entries(rules)) {
    const sub = out.find((o) => o.id === by);
    const first = out.find((o) => o.id !== undefined && o.id !== by && olds.includes(o.id));
    if (!sub || !first) continue;
    const named: Option = { ...sub, label: first.label, hint: sub.hint ?? sub.label };
    out = out.flatMap((o) => (o === sub ? [] : o === first ? [named] : o.id !== undefined && olds.includes(o.id) ? [] : [o]));
    for (const o of opts) if (o.id !== undefined && olds.includes(o.id)) replaced.set(o.id, sub.value).set(o.value, sub.value);
  }
  return { options: out, replaced };
}

/** The runs a slot tries, in order. A slot whose best run is already on screen in another slot
 *  (Stroke's main slot falls back to the flow when there's no tissue run) stays empty, rather than
 *  showing an older export beside it. */
export function slotPaths(candidates: string[], values: Record<string, string>, used: ReadonlySet<string>): string[] {
  const paths = candidates.map((c) => expand(c, values)).filter((p): p is string => !!p);
  const shown = paths.findIndex((p) => used.has(p));
  return shown < 0 ? paths : paths.slice(0, shown);
}

/** Whether a slot's runs were made for the scene's template values (its `when`; every slot without one). */
export const madeFor = (when: Record<string, string> | undefined, values: Record<string, string>) => !when || Object.entries(when).every(([k, v]) => values[k] === v);

/** Each choice's options (an index choice lists its run directory's index), and whether runs exist for
 *  them; `vars` fills placeholders that aren't choices ({site}, from the Stroke slice). */
export async function choiceOptions(
  src: SimSource,
  sc: Scenario,
  chosen: Record<string, string>,
  vars: Record<string, string> = {},
): Promise<{ options: Options; values: Record<string, string>; fixed: Record<string, string> }> {
  const options: Options = {};
  const values: Record<string, string> = {};
  for (const ch of sc.choices ?? []) {
    let replaced = new Map<string, string>();
    if (ch.index) {
      let rows = null;
      for (const dir of ch.index) if ((rows = await readIndex(src, dir))?.length) break;
      // Named options pick runs from the index by id, under their own labels; else every run of the kinds asked for.
      const listed = rows ?? [];
      if (ch.options) options[ch.id] = ch.options.flatMap((o) => listed.filter((r) => r.id === o.value).map((r) => ({ ...o, value: r.path, available: true, id: r.id })));
      else
        ({ options: options[ch.id], replaced } = supersede(
          listed.filter((r) => !ch.kinds || (r.kind && ch.kinds.includes(r.kind))).map((r) => {
            const label = typeof r.name === 'string' ? r.name : typeof r.scenario === 'object' && r.scenario?.label ? r.scenario.label : r.id;
            const hint = typeof r.scenario === 'object' && r.scenario?.label ? r.scenario.label : undefined;
            return { value: r.path, label, ...(hint && hint !== label ? { hint } : {}), available: true, id: r.id };
          }),
          ch.supersede,
        ));
    } else options[ch.id] = (ch.options ?? []).map((o) => ({ ...o, available: true }));
    const opts = options[ch.id]!;
    const asked = chosen[ch.id] ?? ch.default;
    const want = asked !== undefined ? (replaced.get(asked) ?? asked) : undefined;
    values[ch.id] = (opts.find((o) => o.value === want) ?? opts.find((o) => o.id === want) ?? opts[0])?.value ?? '';
  }
  // What the chosen options fix (v5's one collateral grade) wins over the slice's values; `but`
  // leaves one choice's own out, for trying its other options.
  const fixes = (but?: string) => {
    const f: Record<string, string> = {};
    for (const ch of sc.choices ?? []) if (ch.id !== but) Object.assign(f, options[ch.id]?.find((o) => o.value === values[ch.id])?.sets);
    return f;
  };
  // A fixed option is available when some slot finds a run with it (the other choices held).
  for (const ch of sc.choices ?? []) {
    if (ch.index) continue;
    const uses = (sc.sims ?? []).filter((sl) => sl.candidates.some((c) => c.includes(`{${ch.id}}`)));
    if (!uses.length) continue;
    for (const o of options[ch.id]!) {
      const trial = { ...vars, ...values, ...fixes(ch.id), ...o.sets, [ch.id]: o.value };
      const paths = uses.flatMap((sl) => sl.candidates.filter((c) => c.includes(`{${ch.id}}`)).map((c) => expand(c, trial))).filter((p): p is string => !!p);
      o.available = !!(await anyExists(src, paths));
    }
    // A value with no run moves to the nearest option that has one, except a value the Stroke slice
    // sets: that one stays (a distal clot has no 1 h arm), and the panel says there's no run for it,
    // rather than showing another arm's run beside the slice's clot.
    const at = options[ch.id]!.findIndex((o) => o.value === values[ch.id]);
    if (at >= 0 && !options[ch.id]![at]!.available && !ch.fromSlice) {
      const near = options[ch.id]!.map((o, i) => ({ o, d: Math.abs(i - at) })).filter((x) => x.o.available).sort((a, b) => a.d - b.d)[0];
      if (near) values[ch.id] = near.o.value;
    }
  }
  const fixed = fixes();
  return { options, values: { ...values, ...fixed }, fixed };
}

/** A pharma run's PK course, its targets' binding, its dose, and whether the course follows the model the
 *  Dose slider rescales by. Read once a run. */
interface PharmaFit {
  course: PkCourse;
  curves: BindingCurve[];
  dose: CourseDose;
  fits: boolean;
}
const fits = new WeakMap<PharmaSim, Promise<PharmaFit | null>>();
function pharmaFit(p: PharmaSim): Promise<PharmaFit | null> {
  let f = fits.get(p);
  if (!f) {
    f = (async () => {
      const dose = courseDose(p.header);
      if (!dose || p.snapshot || !p.header.files?.series?.length) return null;
      const [series, curves] = await Promise.all([p.series(), p.bindingCurves()]);
      const course = pkCourse(series);
      return { course, curves, dose, fits: courseFits(course, curves) };
    })().catch((err: unknown) => {
      console.warn('[sims] pharma course', err);
      return null;
    });
    fits.set(p, f);
  }
  return f;
}

/** The store's word on a pharma run's course (DrugState), for the panel and the clock-free readers. */
export async function drugState(p: PharmaSim): Promise<DrugState | null> {
  const f = await pharmaFit(p);
  return f ? { run: p.header.id, dose: f.dose.mass, unit: f.dose.unit, fits: f.fits } : null;
}

/** The engagement overlays at the course's own dose, built once a run and target. */
const ownDose = new WeakMap<PharmaSim, Map<string, Promise<Overlay[]>>>();

/**
 * A pharma run's engagement per parcel, and the same by DKT region where the HD cortex's crosswalk
 * is published: the pack in view draws whichever its structures key (v0 has DKT parcels, no Schaefer).
 * At another dose (the Dose slider, where the course follows the model) each frame's values scale by the
 * target's occupancy ratio: engagement is occupancy × density, and a region's value is a mean of its
 * parcels' in the same frame, so both scale exactly.
 */
async function pharmaOverlays(p: PharmaSim, target: string, id: string, dose: number | null = null): Promise<Overlay[]> {
  let byTarget = ownDose.get(p);
  if (!byTarget) ownDose.set(p, (byTarget = new Map()));
  const key = `${id}|${target}`;
  let own = byTarget.get(key);
  if (!own) byTarget.set(key, (own = pharmaOverlaysAtOwnDose(p, target, id)));
  const base = await own;
  if (dose === null) return base;
  const f = await pharmaFit(p);
  if (!f?.fits || dose === f.dose.mass || !(f.dose.mass > 0)) return base;
  const T = f.course.occupancy.targets;
  const k = T.indexOf(target);
  const central = f.course.occupancy.central;
  const scaled = scaledCourse(f.course, f.curves, dose / f.dose.mass).occupancy.central;
  if (k < 0 || !central || !scaled) return base;
  const column = (a: Float32Array) => Float32Array.from({ length: a.length / T.length }, (_, i) => a[i * T.length + k]!);
  const before = column(central);
  const after = column(scaled);
  return base.map((o) => (o.times.length === before.length ? { ...o, values: scaledEngagement(o.values, o.keys.length, before, after) } : o));
}

async function pharmaOverlaysAtOwnDose(p: PharmaSim, target: string, id: string): Promise<Overlay[]> {
  const parcels = await pharmaOverlay(p, target, id);
  if (!parcels) return [];
  const cw = await crosswalk(HD_CORTEX, 'schaefer400', 'dkt', packs).catch((err: unknown) => {
    console.warn('[sims] no DKT crosswalk', err);
    return null;
  });
  const regions = cw ? regionOverlay(parcels, cw, schaeferColumn(p.structureIds), `${id}:dkt`) : null;
  return regions ? [parcels, regions] : [parcels];
}

/** The runs' overlays: hemo's vessels by the Vessels section's quantity (none: the arteries' own colours),
 *  dynamics' activity, pharma's engagement of the target picked. */
async function overlaysFor(slots: [SimSlot, OpenSim][], target: string | undefined, colourBy: StrokeState['colourBy'], dose: number | null = null): Promise<Overlay[]> {
  const out: Overlay[] = [];
  for (const [slot, o] of slots) {
    if (slot.appOnly) continue;
    const ov =
      o.format === 'hemo'
        ? colourBy === 'none'
          ? null
          : await hemoOverlay(o.sim, colourBy, `${slot.slot}:${colourBy}`)
        : o.format === 'dynamics'
          ? await dynamicsOverlay(o.sim, `${slot.slot}:activity`)
          : o.format === 'pharma'
            ? await pharmaOverlays(o.sim, target && o.sim.targets.includes(target) ? target : (o.sim.primaryTarget ?? ''), `${slot.slot}:engagement`, slot.slot === 'main' ? dose : null)
            : null;
    if (Array.isArray(ov)) out.push(...ov);
    else if (ov) out.push(ov);
  }
  return out;
}

/** The scene's clock: its time, speed and whether it plays at its run's pacing. */
export interface SceneClock {
  time: number;
  speed: number;
  paced: boolean;
}

/**
 * A scene clock fitted to its main run's timeline. Entering a scene, its view's own rate (Stroke's
 * Depolarizations at 1 min/s), else the scene's default, even where the fallback timeline's first speed
 * is offered too (Stroke's 1 min/s, which would play its 24 h in 24 min); after a choice, whatever was
 * playing, where it's still offered. A clip's own pacing is the default where the run has one (slow
 * motion around its events), and the time stays inside the run.
 */
export function fitClock(c: SceneClock, tl: Timeline, viewSpeed: number | undefined, entering: boolean, rate?: number): SceneClock {
  const out = { ...c };
  if (viewSpeed !== undefined && tl.speeds.includes(viewSpeed)) Object.assign(out, { speed: viewSpeed, paced: false });
  else if (entering || !tl.speeds.includes(out.speed)) Object.assign(out, { speed: defaultSpeed(tl, rate), paced: false });
  if (tl.pacing) out.paced = true;
  if (out.time > tl.duration) out.time = tl.periodic ? out.time % tl.duration : tl.duration;
  if (tl.start && out.time < tl.start) out.time = tl.start;
  return out;
}

async function resolve(gen: number) {
  const s = useAtlas.getState();
  const sc = scenarioById(s.scenario);
  const stale = () => gen !== generation || useAtlas.getState().scenario !== sc.id;
  // The scene's first runs, not a choice changed in it: the playback rate starts over (below).
  const entering = !(s.sims.scenario === sc.id && s.sims.slots.length > 0);
  opened.clear();
  if (!sc.sims?.length) {
    s.setSims({ scenario: sc.id, status: 'idle', slots: [], options: {} });
    s.setOverlays([]);
    return;
  }
  s.setSims({ ...s.sims, scenario: sc.id, status: 'loading', slots: s.sims.scenario === sc.id ? s.sims.slots : [] });
  try {
    const vars = strokeChoices(s);
    const { options, values, fixed } = await choiceOptions(source, sc, { ...s.choices, ...vars }, vars);
    if (stale()) return;
    const slots: [SimSlot, OpenSim][] = [];
    const used = new Set<string>();
    const at = { ...vars, ...values };
    for (const spec of sc.sims) {
      if (!madeFor(spec.when, at)) continue;
      const o = await resolveFirst(source, slotPaths(spec.candidates, at, used));
      if (stale()) return;
      if (!o) continue;
      used.add(o.path);
      opened.set(spec.slot, o);
      // Markers its reader finds in the run's own files: a cells run's spikes, a seizure run's onset.
      const onset = o.format === 'dynamics' ? await seizureOnset(o.sim).catch(() => null) : null;
      if (stale()) return;
      const events = o.format === 'cells' ? o.sim.markers() : onset !== null ? [{ t: onset, label: 'Onset' }] : null;
      const annotation = await annotationOf(source, o.path).catch(() => null);
      if (stale()) return;
      slots.push([{ slot: spec.slot, ...(spec.label ? { label: spec.label } : {}), path: o.path, format: o.format, header: o.header, standIn: isStandIn(o.header, o.path), appOnly: !!spec.appOnly, ...(events ? { events } : {}), ...(annotation ? { annotation } : {}) }, o]);
    }
    const now = useAtlas.getState();
    const overlays = await overlaysFor(slots, now.traceView.target, now.stroke.colourBy, now.drugDose);
    const main = slots.find(([x]) => x.slot === 'main')?.[1];
    const drug = main?.format === 'pharma' ? await drugState(main.sim) : null;
    if (stale()) return;
    useAtlas.getState().setDrug(drug);
    const next: SimsState = { scenario: sc.id, status: 'ready', slots: slots.map(([x]) => x), options, values, fixed };
    useAtlas.getState().setSims(next);
    useAtlas.getState().setOverlays(overlays);
    // The main sim's time base now drives the scene's timeline: offer its speeds and keep time inside it.
    // In a view on its own pack, which plays that pack's run on its own clock, the scene's clock waits in
    // `sceneClock` for its views: that one is fitted, and the view's is left alone.
    const st = useAtlas.getState();
    const tl = sceneTimelineOf(st);
    const viewSpeed = entering ? sc.presets.find((p) => p.id === st.preset)?.speed : undefined;
    const rate = opened.get('main')?.header.time?.playback_rate;
    if (st.viewPack) {
      const waiting = st.sceneClock ?? { time: sc.start ?? 0, speed: st.speed, paced: false };
      if (tl) useAtlas.setState({ sceneClock: fitClock(waiting, tl, viewSpeed, entering, rate) });
    } else if (tl) {
      const c = fitClock({ time: st.time, speed: st.speed, paced: st.paced }, tl, viewSpeed, entering, rate);
      if (c.speed !== st.speed || c.paced !== st.paced) useAtlas.setState({ speed: c.speed, paced: c.paced });
      if (c.time !== st.time) st.setTime(c.time);
    } else if (st.playing) st.setPlaying(false);
  } catch (err) {
    if (stale()) return;
    console.warn('[sims]', err);
    useAtlas.getState().setSims({ scenario: sc.id, status: 'error', slots: [], options: {}, error: err instanceof Error ? err.message : String(err) });
    useAtlas.getState().setOverlays([]);
  }
}

/** Rebuild the overlays alone (a pharma target or dose, the vessels' quantity) without re-resolving the
 *  runs. Only the latest refresh lands: a dragged Dose slider starts several. */
let refreshes = 0;
async function refreshOverlays(gen: number) {
  const seq = ++refreshes;
  const s = useAtlas.getState();
  const slots = s.sims.scenario === s.scenario ? s.sims.slots : [];
  const pairs = slots.map((x) => [x, opened.get(x.slot)] as const).filter((p): p is [SimSlot, OpenSim] => !!p[1]);
  const overlays = await overlaysFor(pairs, s.traceView.target, s.stroke.colourBy, s.drugDose);
  if (gen === generation && seq === refreshes) useAtlas.getState().setOverlays(overlays);
}

let stopCurrent: (() => void) | null = null;

/** Start following the scene (again: a second start replaces the first). Returns a stop function. */
export function startSims(src: SimSource = defaultSource(), packSrc: SimSource = packSource()): () => void {
  stopCurrent?.();
  source = src;
  packs = packSrc;
  // The Stroke slice names its tissue run by site, grade and treatment; dragging the clot along its
  // vessel changes none of them, so it never re-resolves the runs.
  const offScene = useAtlas.subscribe(
    (s) => [s.scenario, s.choices, choiceKey(strokeChoices(s))] as const,
    () => void resolve(++generation),
    { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2], fireImmediately: true },
  );
  const offTarget = useAtlas.subscribe(
    (s) => [s.traceView.target, s.stroke.colourBy, s.drugDose] as const,
    () => void refreshOverlays(generation),
    { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] },
  );
  const stop = () => {
    offScene();
    offTarget();
    generation++;
    if (stopCurrent === stop) stopCurrent = null;
  };
  stopCurrent = stop;
  return stop;
}
